import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import authSelectors from 'redux/auth/authSelectors';
import appSelectors from 'redux/app/appSelectors';

import authOperations from 'redux/auth/authOperations';

import RouteNames from 'router/routes';

import SharedLayout from 'components/sharedLayout';
import LoaderSpinner from 'components/loaderSpinner';
import PopupMaintanence from 'components/popupMaintanence';

const displayMaintenance = import.meta.env.VITE_MAINTANENCE;

// AUTH
const Signup = lazy(() => import('pages/auth/signup'));
const Login = lazy(() => import('pages/auth/login'));
const ForgotPassword = lazy(() => import('pages/auth/forgotPassword'));
const PasswordReset = lazy(() => import('pages/auth/passwordReset'));

// FOOTER
const PublicOffer = lazy(() => import('pages/common/publicOffer'));
const PrivacyPolicy = lazy(() => import('pages/common/privacyPolicy'));

// ACCOUNT
const Vps = lazy(() => import('pages/acc/vps'));
const Monitoring = lazy(() => import('pages/acc/monitoring'));
const Subscription = lazy(() => import('pages/acc/monitoring/subscription'));
const Accounts = lazy(() => import('pages/acc/monitoring/accounts'));
const Account = lazy(() => import('pages/acc/monitoring/account'));
const AccountPublic = lazy(() => import('pages/acc/monitoring/accountPublic'));
const Guide = lazy(() => import('pages/acc/monitoring/guide'));
const Partners = lazy(() => import('pages/acc/partners'));
const Operations = lazy(() => import('pages/acc/operations'));
const Support = lazy(() => import('pages/acc/support'));
const Ticket = lazy(() => import('pages/acc/ticket'));
const Profile = lazy(() => import('pages/acc/profile'));
const Faq = lazy(() => import('pages/acc/faq'));

const authRoutes = (
  <>
    <Route path={RouteNames.LOGIN} element={<Login />} />
    <Route path={RouteNames.SIGNUP} element={<Signup />} />
    <Route path={RouteNames.FORGOT_PASSWORD} element={<ForgotPassword />} />
    <Route path={RouteNames.PASSWORD_RESET} element={<PasswordReset />} />
  </>
);

const commonRoutes = (
  <>
    <Route path={RouteNames.OFFER} element={<PublicOffer />} />
    <Route path={RouteNames.POLICY} element={<PrivacyPolicy />} />
    <Route path={RouteNames.MONITORING}>
      <Route path={RouteNames.MONITORING_ACCOUNT} element={<AccountPublic />} />
    </Route>
  </>
);

const accRoutes = (
  <>
    <Route path={RouteNames.VPS} element={<Vps />} />

    <Route path={RouteNames.MONITORING} element={<Monitoring />}>
      <Route index element={<Subscription />} />
      <Route path={RouteNames.ACCOUNTS} element={<Accounts />} />
      <Route path={RouteNames.GUIDE} element={<Guide />} />
    </Route>

    <Route path={RouteNames.MONITORING}>
      <Route path={RouteNames.MONITORING_ACCOUNT} element={<Account />} />
    </Route>

    <Route path={RouteNames.PARTNERS} element={<Partners />} />
    <Route path={RouteNames.OPERATIONS} element={<Operations />} />
    <Route path={RouteNames.SUPPORT}>
      <Route index element={<Support />} />
      <Route path={RouteNames.TICKET} element={<Ticket />} />
    </Route>
    <Route path={RouteNames.PROFILE} element={<Profile />} />
    <Route path={RouteNames.FAQ} element={<Faq />} />
  </>
);

const publicRoutes = (
  <>
    <Route path={RouteNames.HOME} element={<SharedLayout />}>
      <Route index element={<Navigate to={RouteNames.LOGIN} replace />} />
      {authRoutes}
      {commonRoutes}
    </Route>
    <Route
      path={RouteNames.ANY}
      element={<Navigate to={RouteNames.HOME} replace />}
    />
  </>
);

const privateRoutes = (
  <>
    <Route path={RouteNames.HOME} element={<SharedLayout />}>
      <Route index element={<Navigate to={RouteNames.VPS} replace />} />
      {accRoutes}
      {commonRoutes}
    </Route>
    <Route
      path={RouteNames.ANY}
      element={<Navigate to={RouteNames.HOME} replace />}
    />
  </>
);

function Router() {
  const dispatch = useDispatch();
  const isAuthorized = useSelector(authSelectors.isAuthorized);
  const displayLoader = useSelector(appSelectors.displayLoader);

  const routes = isAuthorized ? privateRoutes : publicRoutes;

  useEffect(() => {
    if (isAuthorized && displayMaintenance) dispatch(authOperations.logout());
  }, [dispatch, isAuthorized]);

  return (
    <Suspense fallback={<LoaderSpinner />}>
      <Routes>{routes}</Routes>
      {displayMaintenance && <PopupMaintanence />}
      {displayLoader && <LoaderSpinner />}
    </Suspense>
  );
}

export default Router;
